import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useAuth } from './AuthContext';
import { getMyCart, saveMyCart } from '../lib/api';

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem('zs_cart');
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [open, setOpen] = useState(false);
  const loadedRef = useRef(false);

  useEffect(() => {
    localStorage.setItem('zs_cart', JSON.stringify(items));
  }, [items]);

  // On login: merge server cart with local cart (union by name)
  useEffect(() => {
    if (!user) { loadedRef.current = false; return; }
    let cancelled = false;
    getMyCart().then(({ items: serverItems }) => {
      if (cancelled) return;
      setItems(prev => {
        const map = new Map();
        (serverItems || []).forEach(i => map.set(i.name, { ...i }));
        prev.forEach(i => { if (!map.has(i.name)) map.set(i.name, i); });
        return Array.from(map.values());
      });
      loadedRef.current = true;
    }).catch(() => { loadedRef.current = true; });
    return () => { cancelled = true; };
  }, [user]);

  // Persist cart to server (debounced) while logged in
  useEffect(() => {
    if (!user || !loadedRef.current) return;
    const t = setTimeout(() => { saveMyCart(items).catch(() => {}); }, 600);
    return () => clearTimeout(t);
  }, [items, user]);

  const add = useCallback((item) => {
    setItems(prev => {
      const idx = prev.findIndex(p => p.name === item.name);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: next[idx].qty + 1 };
        return next;
      }
      return [...prev, { name: item.name, price: item.price, qty: 1, image_url: item.image_url || item.img }];
    });
  }, []);

  // Add multiple items at once (used by "Reorder")
  const addItems = useCallback((list) => {
    setItems(prev => {
      const map = new Map(prev.map(p => [p.name, { ...p }]));
      (list || []).forEach(it => {
        const qty = it.qty || 1;
        if (map.has(it.name)) map.set(it.name, { ...map.get(it.name), qty: map.get(it.name).qty + qty });
        else map.set(it.name, { name: it.name, price: it.price, qty, image_url: it.image_url || it.img || '' });
      });
      return Array.from(map.values());
    });
  }, []);

  const remove = useCallback((name) => {
    setItems(prev => prev.filter(p => p.name !== name));
  }, []);

  const setQty = useCallback((name, qty) => {
    setItems(prev => qty <= 0 ? prev.filter(p => p.name !== name) : prev.map(p => p.name === name ? { ...p, qty } : p));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const { count, total } = useMemo(() => {
    let count = 0, total = 0;
    items.forEach(i => { count += i.qty; total += i.qty * i.price; });
    return { count, total };
  }, [items]);

  const value = { items, add, addItems, remove, setQty, clear, count, total, open, setOpen };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside CartProvider');
  return ctx;
};
